import { useState, useRef, useEffect, useCallback } from "react";
import { useParams } from 'react-router-dom';
import clsx from 'clsx';

import Grid from '@material-ui/core/Grid'
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';

import ButtonPrimary from "@base/Button";
import Title from '@base/Title';
import Toast from '@base/Toast';
import TextField from '@base/Inputs/Text';
import ButtonHelp from "@base/Button/ButtonHelp";

import ObjetoPostalService from '@services/ObjetoPostalService';
import CaixaService from '@services/CaixaService';

import PortariaTableView from '@pages/Portaria/PortariaTableView'
import AbrirNovaCaixaDialog from '@pages/Portaria/AbrirNovaCaixaDialog'
import EncaminharObjetoPostalDialog from '@base/Dialog/ObjetoPostal/EncaminharObjetoPostalDialog'

import { ProcessoResolver } from '@helpers/ProcessoHelper';

import AddIcon from '@material-ui/icons/Add';
import {
	isNotNumber,
	isNotObjetoPostal
} from "@helpers/ValidationHelper";

const __ENVELOPE_LENGTH = 13;

const useStyles = makeStyles(
	(theme) => (
		{
			wrapper: {
				padding: theme.spacing(3),
			},
			space: {
				margin: theme.spacing(2, 0),
			},
			title: {
				color: "#333",
				fontSize: 24,
			}
		}
	)
);

export const Portaria = () => {
	const classes = useStyles();

	const [loading, setLoading] = useState(false);
	const [caixaCodigo, setCaixaCodigo] = useState('');
	const [numeroObjetoPostal, setNumeroObjetoPostal] = useState('');
	const [formularioSelecionado, setFormularioSelecionado] = useState(false);
  const [statusArmazenada, setStatusArmazenada] = useState(false);
	const [openDialogAbrirNovaCaixa, setOpenDialogAbrirNovaCaixa] = useState(false);
  const [openEncaminharObjetoPostalDialog, setOpenEncaminharObjetoPostalDialog] = useState(false);
	const [disableInputCaixa, setDisableInputCaixa] = useState(false);

  const [mensagem, setMensagem] = useState('');
  const [statusMensagem, setStatusMensagem] = useState('');

	const [filtros, setFiltros] = useState([]);
	const [filtroAtivo, setFiltroAtivo] = useState(false);

	const params = useParams();
	const processo = ProcessoResolver(params.processo);

	const toastRef = useRef(null);
	const dialogAbrirNovaCaixaRef = useRef(null);

	const isCaixaInvalida = (id) => {
		if (!id) return `<li>O campo 'Código da Caixa' é obrigatório.</li>`;
		if (isNotNumber(id)) return `<li>O campo 'Código da Caixa' deve ser Numérico.</li>`;
	}

	const isObjetoPostalInvalido = (id) => {
		if (!id) return `<li>O campo 'Número do Objeto Postal' é obrigatório.</li>`;
		if (isNotObjetoPostal(id)) return `<li>O campo 'Número do Objeto Postal' precisa estar no formato <b>XX000000000XX</b>.</li>`;
	}

	const limparCamposParaAdicionarOutroEnvelope = () => {
		setDisableInputCaixa(true);
		setFormularioSelecionado(true);
		setNumeroObjetoPostal('');
	}

	const carregaCaixa = (id) => {
		setCaixaCodigo(id);
		const caixaInvalida = isCaixaInvalida(id);

		if (caixaInvalida) {
			toastRef.current.show("error", caixaInvalida);
			return
		}

		if(id.length === 4){
			CaixaService.buscarPorId(processo.id, id)
				.then(resposta => {
					if (resposta.status === 200) {

						setStateAposCarregarCaixa(resposta.data.codigo);

            resposta.data.status === "ARMAZENADA" ? setStatusArmazenada(true) : setStatusArmazenada(false);

					} else {
						CaixaService.adicionar(processo.id, { conteudo: 'FISICO', codigo: parseInt(id) })
							.then(caixa => setStateAposCarregarCaixa(caixa.data.codigo));
					}
			});
		}
	}

	const setStateAposCarregarCaixa = (codigo) => {
		setDisableInputCaixa(true);
		setFormularioSelecionado(true);
		fecharDialog();
	}

	const fecharDialog = () => {
		if (openDialogAbrirNovaCaixa) {
			dialogAbrirNovaCaixaRef.current.closeDialog();
			setOpenDialogAbrirNovaCaixa(false);
			document.querySelector('#objetoPostal').focus();
		}
	}

	const adicionaEnvelopeHaCaixa = (id) => {
		id = id?.toUpperCase();

		setNumeroObjetoPostal(id);
		setLoading(true);

		if (id.length < 13) {
			setLoading(false);
			return
		}

		const objetoPostalInvalido = isObjetoPostalInvalido(id);

		if (objetoPostalInvalido) {
			toastRef.current.show("error", objetoPostalInvalido);
			setLoading(false);
			return
		}

		ObjetoPostalService.getByProcessoAndNumeroObjetoPostal(processo.id, id)
			.then(result => {
				setLoading(false);

        if (result.data.caixaCodigo == null){
          setMensagem(`Envelope '${id}' já foi recebido, mas está sem caixa definida.`);
        } else if(result.data.caixaCodigo === parseInt(caixaCodigo)){
          setMensagem(`Envelope '${id}' já foi recebido na caixa atual: #${caixaCodigo}.`);
        }else {
          setMensagem(`Envelope '${id}' já foi recebido em outra caixa: #${result.data.caixaCodigo}.`);
        }

        switch (result.data.status) {
          case "RECEBIDO":
            setStatusMensagem('Triagem');
            break;
          case "TRIADO":
            setStatusMensagem('Lançamento');
            break;
          case "LANCADO":
            setStatusMensagem('Digitalização');
            break;
          case "EM_RECONTAGEM":
            setStatusMensagem('Recontagem');
            break;
          case "FINALIZADO":
            setStatusMensagem('Armazenamento');
			break;
			default:
				break;
        }

        setOpenEncaminharObjetoPostalDialog(true);

			}).catch(error => {
				const objetoPostal = {
					'processo': processo.id,
					'numero': id,
					'caixa': caixaCodigo
				};

				ObjetoPostalService.registrarRecebimento(objetoPostal)
					.then(() => {
						toastRef.current.show("success", `Envelope ${id} recebido com sucesso.`);
						aplicarFiltros();
					})
					.catch(error => toastRef.current.show("error", error.response.data.mensagens !== undefined ? error.response.data.mensagens.map(mensagem => mensagem).join(" ,") :  "Recebimento não registrado"))
					.finally(() => {
						setLoading(false);
						limparCamposParaAdicionarOutroEnvelope();
					});
			});
	}

	const aplicarFiltros = useCallback(() => {
		let f = [];

		f.push({ field: 'codigoCaixa', value: caixaCodigo });
		f.push({ field: 'conteudo', value: 'FISICO' });
		f.push({ field: 'origem', value: 'ENVELOPE' });

		setFiltros(f);
		setFiltroAtivo(false);
	},[caixaCodigo])

	useEffect(() => {
		if (caixaCodigo)
			aplicarFiltros();
	}, [ caixaCodigo, aplicarFiltros ]);

	return (
		<>
			<Toast ref={toastRef} />
			<Title>Recebimento de Envelopes <ButtonHelp tipo="RECEBIMENTO" /></Title>

			<Paper className={classes.wrapper} elevation={0} spacing={2}>
				<Grid alignItems="center" container>
					<Grid item sm={2}>
						<TextField
							autoFocus
							inputProps={{
								maxLength: 10
							}}
							label={"Código da Caixa"}
							value={caixaCodigo}
							onChange={(e) => carregaCaixa(e.target.value)}
							disabled={disableInputCaixa}
							helperText="Exemplo de Formato: XXXX"
							onKeyPress={(e) => e}
						/>
					</Grid>
				</Grid>
			</Paper>

			{formularioSelecionado &&
				<>
					<Paper className={classes.wrapper} elevation={0} spacing={2}>
						<Grid alignItems="center" container>
							<Grid item sm={4}>
								<TextField
									autoFocus
									id="objetoPostal"
									inputProps={{ maxLength: __ENVELOPE_LENGTH }}
									label={"Número do Objeto Postal"}
									value={numeroObjetoPostal}
									onChange={(e) => adicionaEnvelopeHaCaixa(e.target.value)}
									onKeyPress={(e) => e}
									disabled={statusArmazenada}
									helperText="Exemplo de Formato: XX000000000XX"
								/>
							</Grid>
						</Grid>
					</Paper>

					<Paper className={clsx(classes.wrapper, classes.space)}>
						<Title style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
							<b>Envelopes na CAIXA {caixaCodigo} {statusArmazenada ? " (ARMAZENADA)" : ""}</b>
							<ButtonPrimary
								style={{ marginLeft: '24px' }}
								onClick={() => setOpenDialogAbrirNovaCaixa(true)}
								name="Abrir Nova Caixa"
								startIcon={loading ? <CircularProgress size={16} /> : <AddIcon />}
								disabled={loading}
							/>
						</Title>

						<Grid alignItems="center" container spacing={2}>
							<PortariaTableView
								processo={processo.id}
								caixaCodigo={caixaCodigo}
								filtros={filtros}
								filtroAtivo={filtroAtivo}
								setFiltroAtivo={setFiltroAtivo}
							/>
						</Grid>
					</Paper>
				</>
			}

			{openDialogAbrirNovaCaixa &&
				<AbrirNovaCaixaDialog
					aoFechar={() => setOpenDialogAbrirNovaCaixa(false)}
					aberto={openDialogAbrirNovaCaixa}
					handlerCarregaCaixa={carregaCaixa}
					ref={dialogAbrirNovaCaixaRef}
				/>
			}

      {openEncaminharObjetoPostalDialog &&
        <>
          <EncaminharObjetoPostalDialog
            caixaAtual={caixaCodigo}
            mensagem ={mensagem}
            statusMensagem={statusMensagem}
            close={() => {
              limparCamposParaAdicionarOutroEnvelope();
              setOpenEncaminharObjetoPostalDialog(false);
              setMensagem('');
              setStatusMensagem('');
            }}
            open={openEncaminharObjetoPostalDialog}
          />
        </>
      }
		</>
	);
}

export default Portaria;
